import { getInterestIcon } from "@/components/mentor-profile/interest-icons";
import { SpotifyVinyl } from "@somethings-ui/web";
import { AudioDialog } from "@/components/shared/audio-dialog";
import { MatchCardPhotoSection } from "@/components/users/user-details/match-card-photo-section";
import { Form } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import {
  useUploadMentorImage,
  type MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import { useUpdateProfileAudio, type User } from "@/lib/api/user";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user";
import {
  DEFAULT_MENTOR_LANGUAGE,
  DISPLAY_NAME_MAX_LENGTH,
  isValidSpotifyLink,
  MENTOR_INTEREST_OPTIONS,
  MENTOR_LANGUAGE_OPTIONS,
  VIBE_SLOT_COUNT,
} from "@/lib/mentor-profile/constants";
import { uploadVerticalMentorImage } from "@/lib/mentor-profile/upload-vertical-image";
import { pronounOptions } from "@/lib/strings/profile-data";
import { cn } from "@/lib/utils";
import { ChevronRight, Copy, ExternalLink, Loader2, Plus, X } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

type ProfileField =
  | "photo"
  | "name"
  | "languages"
  | "bio"
  | "spotify"
  | "audio"
  | "vibe"
  | "interests"
  | "pronouns";

export function toggleOpenProfileFields(
  open: ReadonlySet<ProfileField>,
  field: ProfileField,
): Set<ProfileField> {
  const next = new Set(open);
  if (next.has(field)) next.delete(field);
  else next.add(field);
  return next;
}

export function closeOpenProfileField(
  open: ReadonlySet<ProfileField>,
  field: ProfileField,
): Set<ProfileField> {
  if (!open.has(field)) return new Set(open);
  const next = new Set(open);
  next.delete(field);
  return next;
}

interface RecordProfileMatchCardProps {
  user: User;
  profile: MentorProfileDraft;
  title?: string;
  readOnly?: boolean;
}

export function RecordProfileMatchCard({
  user,
  profile,
  title = "Match card",
  readOnly = false,
}: RecordProfileMatchCardProps) {
  const { toast } = useToast();
  const [openFields, setOpenFields] = useState<Set<ProfileField>>(
    () => new Set(),
  );
  const [audioOpen, setAudioOpen] = useState(false);
  const updateAudio = useUpdateProfileAudio(user.id);
  const { handleUpdate, fieldState, form } = useUpdateUser({
    userId: user.id,
    pronouns: user.pronouns || "",
    profile: {
      bio: user.profile?.bio || "",
      interests: user.profile?.interests || [],
      favorite_music: user.profile?.favorite_music || "",
    },
  });
  const saving = Boolean(fieldState.profile || fieldState.pronouns);

  const toggle = (field: ProfileField) => {
    setOpenFields((current) => toggleOpenProfileFields(current, field));
  };

  const closeField = (field: ProfileField) => {
    setOpenFields((current) => closeOpenProfileField(current, field));
  };

  const saveProfile = async (
    field: ProfileField,
    patch: {
      display_name?: string;
      bio?: string;
      interests?: string[];
      languages?: string[];
      favorite_music?: string;
      vibe_images?: Array<{ imageUrl: string; prompt?: string }>;
    },
  ) => {
    const saved = await handleUpdate("profile", {
      bio: user.profile?.bio || "",
      interests: user.profile?.interests || [],
      favorite_music: user.profile?.favorite_music || "",
      ...patch,
    });
    if (saved === false) {
      toast({
        title: "Failed to save changes",
        variant: "destructive",
      });
      return;
    }
    if (!saved) return;
    closeField(field);
  };

  const languages = profile.languages ?? [];
  const interests = profile.interests ?? [];
  const vibeCount = profile.vibe_images?.filter((image) => image.imageUrl).length ?? 0;

  return (
    <div className="w-full min-w-0 flex-1 rounded-[20px] border border-[#dfddd8] bg-white px-6 py-1.5">
      <h3 className="pb-1.5 pt-3.5 font-body text-base font-bold leading-6 text-text-primary">
        {title}
      </h3>
      <ProfileRow
        label="Match card photo"
        readOnly={readOnly}
        open={openFields.has("photo")}
        onToggle={() => toggle("photo")}
        summary={profile.match_card_photo_url ? "Photo added" : "No photo yet"}
      >
        <Form {...form}>
          <MatchCardPhotoSection
            userId={user.id}
            photoUrl={profile.match_card_photo_url}
          />
        </Form>
      </ProfileRow>
      <ProfileRow
        label="Display name"
        readOnly={readOnly}
        open={openFields.has("name")}
        onToggle={() => toggle("name")}
        summary={profile.display_name || "—"}
      >
        <TextSaveField
          initial={profile.display_name || ""}
          maxLength={DISPLAY_NAME_MAX_LENGTH}
          saving={saving}
          onCancel={() => closeField("name")}
          onSave={async (value) => {
            const next = value.trim();
            if (!next) {
              toast({
                title: "Display name is required",
                variant: "destructive",
              });
              return;
            }
            await saveProfile("name", { display_name: next });
          }}
        />
      </ProfileRow>
      <ProfileRow
        label="Languages"
        readOnly={readOnly}
        open={openFields.has("languages")}
        onToggle={() => toggle("languages")}
        summary={
          languages.length > 0 ? <ChipList labels={languages} /> : "—"
        }
      >
        <LanguageEditor
          initial={languages}
          saving={saving}
          onCancel={() => closeField("languages")}
          onSave={(next) => saveProfile("languages", { languages: next })}
        />
      </ProfileRow>
      <ProfileRow
        label="Bio"
        readOnly={readOnly}
        open={openFields.has("bio")}
        onToggle={() => toggle("bio")}
        summary={profile.bio || "—"}
        wrap
      >
        <TextSaveField
          initial={profile.bio || ""}
          multiline
          saving={saving}
          onCancel={() => closeField("bio")}
          onSave={(value) => saveProfile("bio", { bio: value })}
        />
      </ProfileRow>
      <ProfileRow
        label="On repeat (Spotify)"
        readOnly={readOnly}
        open={openFields.has("spotify")}
        onToggle={() => toggle("spotify")}
        summary={
          profile.favorite_music ? (
            <SpotifySummary
              link={profile.favorite_music}
              previewUrl={profile.spotify_preview_url}
              meta={profile.spotify_meta}
            />
          ) : (
            "—"
          )
        }
      >
        <TextSaveField
          initial={profile.favorite_music || ""}
          saving={saving}
          onCancel={() => closeField("spotify")}
          onSave={async (value) => {
            const next = value.trim();
            if (next && !isValidSpotifyLink(next)) {
              toast({
                title: "Add a Spotify track link",
                variant: "destructive",
              });
              return;
            }
            await saveProfile("spotify", { favorite_music: next });
          }}
        />
      </ProfileRow>
      <ProfileRow
        label="Audio intro"
        readOnly={readOnly}
        open={openFields.has("audio")}
        onToggle={() => toggle("audio")}
        summary={profile.profile_audio_url ? "Audio added" : "No audio yet"}
      >
        <button
          type="button"
          className="font-body text-sm font-bold text-text-primary"
          onClick={() => setAudioOpen(true)}
        >
          {profile.profile_audio_url ? "Change audio" : "Upload audio"}
        </button>
        <AudioDialog
          open={audioOpen}
          onClose={() => setAudioOpen(false)}
          title="Audio intro"
          currentAudioUrl={profile.profile_audio_url}
          onUpload={async (file) => {
            await updateAudio.mutateAsync(file);
          }}
        />
      </ProfileRow>
      <ProfileRow
        label="My vibe"
        readOnly={readOnly}
        open={openFields.has("vibe")}
        onToggle={() => toggle("vibe")}
        summary={vibeCount > 0 ? `${vibeCount} photos` : "No photos yet"}
      >
        <VibeEditor
          initial={profile.vibe_images ?? []}
          saving={saving}
          onCancel={() => closeField("vibe")}
          onSave={(images) => saveProfile("vibe", { vibe_images: images })}
        />
      </ProfileRow>
      <ProfileRow
        label="Interests"
        readOnly={readOnly}
        open={openFields.has("interests")}
        onToggle={() => toggle("interests")}
        summary={
          interests.length > 0 ? (
            <ChipList labels={interests} withIcons />
          ) : (
            "—"
          )
        }
      >
        <InterestEditor
          initial={interests}
          saving={saving}
          onCancel={() => closeField("interests")}
          onSave={(next) => saveProfile("interests", { interests: next })}
        />
      </ProfileRow>
      <ProfileRow
        label="Pronouns"
        readOnly={readOnly}
        open={openFields.has("pronouns")}
        onToggle={() => toggle("pronouns")}
        summary={profile.pronouns || "—"}
      >
        <PronounEditor
          initial={profile.pronouns || ""}
          saving={saving}
          onCancel={() => closeField("pronouns")}
          onSave={async (value) => {
            const saved = await handleUpdate("pronouns", value);
            if (saved === false) {
              toast({
                title: "Failed to save changes",
                variant: "destructive",
              });
              return;
            }
            if (!saved) return;
            closeField("pronouns");
          }}
        />
      </ProfileRow>
    </div>
  );
}

function ProfileRow({
  label,
  summary,
  open,
  onToggle,
  wrap = false,
  readOnly = false,
  children,
}: {
  label: string;
  summary: ReactNode;
  open: boolean;
  onToggle: () => void;
  wrap?: boolean;
  readOnly?: boolean;
  children: ReactNode;
}) {
  const expanded = !readOnly && open;
  return (
    <div className="flex items-start gap-8 border-t border-[#dfddd8] py-3.5 first:border-t-0">
      {readOnly ? (
        <p className="w-[148px] shrink-0 pt-1 text-left font-body text-xs font-medium leading-4 text-[#6e6b67]">
          {label}
        </p>
      ) : (
        <button
          type="button"
          className="w-[148px] shrink-0 pt-1 text-left font-body text-xs font-medium leading-4 text-[#6e6b67]"
          onClick={onToggle}
        >
          {label}
        </button>
      )}
      <div className={cn("min-w-0 flex-1", wrap ? "" : "")}>
        {expanded ? (
          children
        ) : typeof summary === "string" ? (
          readOnly ? (
            <span className="font-body text-base leading-5 text-text-primary">
              {summary}
            </span>
          ) : (
            <button
              type="button"
              className="w-full text-left"
              onClick={onToggle}
            >
              <span className="font-body text-base leading-5 text-text-primary">
                {summary}
              </span>
            </button>
          )
        ) : (
          summary
        )}
      </div>
      {readOnly ? null : (
        <button
          type="button"
          className="mt-1 shrink-0"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? `Close ${label}` : `Edit ${label}`}
        >
          <ChevronRight
            className={cn(
              "size-3.5 text-text-secondary transition-transform",
              expanded && "rotate-90",
            )}
          />
        </button>
      )}
    </div>
  );
}

function TextSaveField({
  initial,
  multiline = false,
  maxLength,
  saving,
  onSave,
  onCancel,
}: {
  initial: string;
  multiline?: boolean;
  maxLength?: number;
  saving: boolean;
  onSave: (value: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);

  return (
    <div className="flex flex-col items-start gap-2">
      {multiline ? (
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          className="h-[120px] w-full max-w-[450px] rounded-xl border border-[#8a8275] px-6 py-4 font-body text-base leading-6 text-text-primary"
        />
      ) : (
        <input
          value={value}
          maxLength={maxLength}
          onChange={(event) => setValue(event.target.value)}
          className="h-14 w-full max-w-[450px] rounded-xl border border-[#8a8275] px-6 font-body text-base text-text-primary"
        />
      )}
      <SaveCancel
        saving={saving}
        onCancel={onCancel}
        onSave={() => onSave(value)}
      />
    </div>
  );
}

function LanguageEditor({
  initial,
  saving,
  onSave,
  onCancel,
}: {
  initial: string[];
  saving: boolean;
  onSave: (languages: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState(
    initial.length > 0 ? initial : [DEFAULT_MENTOR_LANGUAGE],
  );
  const options = [
    ...MENTOR_LANGUAGE_OPTIONS,
    ...selected.filter(
      (language) =>
        !MENTOR_LANGUAGE_OPTIONS.includes(
          language as (typeof MENTOR_LANGUAGE_OPTIONS)[number],
        ),
    ),
  ];

  const toggle = (language: string) => {
    if (language === DEFAULT_MENTOR_LANGUAGE) return;
    setSelected((current) =>
      current.includes(language)
        ? current.filter((item) => item !== language)
        : [...current, language],
    );
  };

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex flex-wrap gap-1.5">
        {options.map((language) => {
          const active = selected.includes(language);
          return (
            <button
              key={language}
              type="button"
              onClick={() => toggle(language)}
              className={cn(
                "inline-flex h-8 items-center rounded-full border px-2.5 font-body text-[13px] font-medium",
                active
                  ? "border-transparent bg-text-primary text-white"
                  : "border-[rgba(31,30,29,0.08)] bg-[#f6f4ef] text-text-primary",
              )}
            >
              {language}
            </button>
          );
        })}
      </div>
      <SaveCancel
        saving={saving}
        onCancel={onCancel}
        onSave={() => onSave(selected)}
      />
    </div>
  );
}

function InterestEditor({
  initial,
  saving,
  onSave,
  onCancel,
}: {
  initial: string[];
  saving: boolean;
  onSave: (interests: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState(initial);

  const toggle = (interest: string) => {
    setSelected((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest],
    );
  };

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex flex-wrap gap-1.5">
        {MENTOR_INTEREST_OPTIONS.map((interest) => {
          const active = selected.includes(interest);
          const Icon = getInterestIcon(interest);
          return (
            <button
              key={interest}
              type="button"
              onClick={() => toggle(interest)}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-full border px-2.5 font-body text-[13px] font-medium",
                active
                  ? "border-transparent bg-text-primary text-white"
                  : "border-[rgba(31,30,29,0.08)] bg-[#f6f4ef] text-text-primary",
              )}
            >
              <Icon className="size-3.5" aria-hidden />
              {interest}
            </button>
          );
        })}
      </div>
      <SaveCancel
        saving={saving}
        onCancel={onCancel}
        onSave={() => onSave(selected)}
      />
    </div>
  );
}

function PronounEditor({
  initial,
  saving,
  onSave,
  onCancel,
}: {
  initial: string;
  saving: boolean;
  onSave: (value: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex flex-wrap gap-1.5">
        {pronounOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setValue(option.value)}
            className={cn(
              "inline-flex h-8 items-center rounded-full border px-2.5 font-body text-[13px] font-medium",
              value === option.value
                ? "border-transparent bg-text-primary text-white"
                : "border-[rgba(31,30,29,0.08)] bg-[#f6f4ef] text-text-primary",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      <SaveCancel saving={saving} onCancel={onCancel} onSave={() => onSave(value)} />
    </div>
  );
}

function SaveCancel({
  saving,
  onSave,
  onCancel,
}: {
  saving: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="inline-flex h-8 min-w-[120px] items-center justify-center rounded-full bg-black px-4 font-body text-xs font-bold text-white disabled:opacity-50"
        disabled={saving}
        onClick={() => {
          void onSave();
        }}
      >
        {saving ? "Saving..." : "Save"}
      </button>
      <button
        type="button"
        className="inline-flex h-8 min-w-[120px] items-center justify-center rounded-full border border-[rgba(160,153,141,0.16)] bg-[rgba(160,153,141,0.08)] px-4 font-body text-xs font-bold text-black disabled:opacity-50"
        disabled={saving}
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  );
}

function ChipList({
  labels,
  withIcons = false,
}: {
  labels: string[];
  withIcons?: boolean;
}) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {labels.map((label) => {
        const Icon = withIcons ? getInterestIcon(label) : null;
        return (
          <span
            key={label}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-[rgba(31,30,29,0.08)] bg-[#f6f4ef] px-2.5 font-body text-[13px] font-medium leading-[22px] text-text-primary"
          >
            {Icon ? <Icon className="size-3.5" aria-hidden /> : null}
            {label}
          </span>
        );
      })}
    </span>
  );
}

function VibeEditor({
  initial,
  saving,
  onSave,
  onCancel,
}: {
  initial: Array<{ imageUrl: string; prompt?: string }>;
  saving: boolean;
  onSave: (
    images: Array<{ imageUrl: string; prompt?: string }>,
  ) => Promise<void>;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const upload = useUploadMentorImage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState(initial);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (images.length >= VIBE_SLOT_COUNT) return;
    setUploading(true);
    const result = await uploadVerticalMentorImage(file, {
      filename: file.name || "vibe.jpg",
      upload: (params) => upload.mutateAsync(params),
    });
    setUploading(false);
    if (!result.ok) {
      toast({ title: result.error, variant: "destructive" });
      return;
    }
    setImages((current) => [...current, { imageUrl: result.url }]);
  };

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex flex-wrap gap-2">
        {images.map((image, index) => (
          <VibeThumb
            key={`${image.imageUrl}-${index}`}
            imageUrl={image.imageUrl}
            onRemove={() =>
              setImages((current) => current.filter((_, i) => i !== index))
            }
          />
        ))}
        {images.length < VIBE_SLOT_COUNT ? (
          <button
            type="button"
            className="flex size-24 items-center justify-center rounded-2xl border-2 border-dashed border-text-primary/25"
            disabled={uploading}
            aria-label="Add vibe photo"
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-4" />
            )}
          </button>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleFile(file);
          event.target.value = "";
        }}
      />
      <SaveCancel
        saving={saving || uploading}
        onCancel={onCancel}
        onSave={() => onSave(images)}
      />
    </div>
  );
}

function VibeThumb({
  imageUrl,
  onRemove,
}: {
  imageUrl: string;
  onRemove: () => void;
}) {
  const { url } = useSignedMedia(imageUrl);

  return (
    <div className="relative size-24 overflow-hidden rounded-2xl bg-[#f6f4ef]">
      {url ? <img src={url} alt="" className="size-full object-cover" /> : null}
      <button
        type="button"
        className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-full bg-black/55 text-white"
        aria-label="Remove photo"
        onClick={onRemove}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

function SpotifySummary({
  link,
  previewUrl,
  meta,
}: {
  link: string;
  previewUrl?: string;
  meta?: Record<string, unknown>;
}) {
  const spotifyMeta = meta as
    | { title?: string; artist?: string; artworkUrl?: string }
    | undefined;
  const title = spotifyMeta?.title?.trim() ?? "";
  const artist = spotifyMeta?.artist?.trim() ?? "";
  const label = title ? (artist ? `${title} - ${artist}` : title) : "";

  return (
    <span className="flex items-center gap-3 rounded-[17px] border border-[rgba(31,30,29,0.08)] bg-[#f6f4ef] p-2">
      <span
        className={cn(
          "relative w-24 shrink-0",
          label ? "h-24" : "h-[7.5rem]",
        )}
      >
        <SpotifyVinyl
          spotifyUrl={link}
          previewUrl={previewUrl}
          size="sm"
          className={cn(
            "absolute inset-x-0 top-0",
            label && "[&>p]:hidden",
          )}
        />
      </span>
      {label ? (
        <span className="min-w-0 flex-1 truncate font-body text-sm font-medium text-text-primary">
          {label}
        </span>
      ) : (
        <span className="min-w-0 flex-1" />
      )}
      <span className="flex items-center">
        <button
          type="button"
          className="flex size-8 items-center justify-center"
          aria-label="Copy link"
          onClick={(event) => {
            event.stopPropagation();
            void navigator.clipboard.writeText(link);
          }}
        >
          <Copy className="size-4" />
        </button>
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="flex size-8 items-center justify-center"
          aria-label="Open in Spotify"
          onClick={(event) => event.stopPropagation()}
        >
          <ExternalLink className="size-4" />
        </a>
      </span>
    </span>
  );
}

